/**
 * BlockCanvasPage
 *
 * Full-screen page for viewing/editing block screens via direct URL.
 * Follows the StrategyCanvasPage pattern: renders outside Layout (no sidebar).
 *
 * Route: /project/:projectId/strategy/:strategyId/block/:blockId
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Alert, Button, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { OnboardingCanvasEditorV2 } from '@modules/reactFlow/components/onboarding/OnboardingCanvasEditorV2';
import { StrategyV2ScreenFlowCoordinator } from '@modules/reactFlow/services/strategy/StrategyV2ScreenFlowCoordinator';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import {
  buildBlockCanvasUrl,
  buildBlockScreenCanvasUrl,
  buildStrategyCanvasUrl,
  STRATEGY_SCREEN_MODAL_PARAM,
} from '@modules/navigation/constants/routes';
import type { FunnelVariable } from '@segmently/schema-models/v2';

export function BlockCanvasPage() {
  const { projectId, strategyId, blockId } = useParams<{
    projectId: string;
    strategyId: string;
    blockId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [strategyName, setStrategyName] = useState<string>('');
  const [blockName, setBlockName] = useState<string>('');
  const [resolvedBlockId, setResolvedBlockId] = useState<string>('');
  const [resolvedBlockEntityId, setResolvedBlockEntityId] = useState<string>('');
  const [strategyVariables, setStrategyVariables] = useState<Record<string, FunnelVariable>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const initialScreenId = searchParams.get(STRATEGY_SCREEN_MODAL_PARAM) || searchParams.get('screen') || null;

  // Load strategy + block metadata
  useEffect(() => {
    if (!projectId || !strategyId || !blockId) return;

    const strategyService = new StrategyService(projectId);
    const blockService = new StrategyBlockService(projectId, strategyId);

    Promise.all([
      strategyService.get(strategyId),
      blockService.get(blockId),
    ])
      .then(([strategy, block]) => {
        if (!strategy) {
          setError('Strategy not found');
          return;
        }
        return Promise.resolve(block)
          .then(async (directBlock) => {
            if (directBlock) {
              return { block: directBlock, resolvedId: blockId };
            }

            const blocks = await blockService.list();
            const entityBlock = blocks.find(item => item.entityId === blockId);
            return {
              block: entityBlock ?? null,
              resolvedId: entityBlock?.id ?? blockId,
            };
          })
          .then(({ block: resolvedBlock, resolvedId }) => {
            if (!resolvedBlock) {
              setError('Block not found');
              return;
            }

            setStrategyName(strategy.name || 'Strategy');
            setBlockName(resolvedBlock.name || resolvedBlock.blockType || 'Block');
            setResolvedBlockId(resolvedId);
            setResolvedBlockEntityId(resolvedBlock.entityId || resolvedId);
            setStrategyVariables((strategy as any)?.variables ?? {});
          });
      })
      .catch((err) => {
        console.error('[BlockCanvasPage] Failed to load:', err);
        setError('Failed to load strategy or block');
      })
      .finally(() => setLoading(false));
  }, [projectId, strategyId, blockId]);

  const handleClose = () => {
    if (projectId && strategyId) {
      navigate(buildStrategyCanvasUrl(projectId, strategyId));
    } else {
      navigate(-1);
    }
  };

  if (!projectId || !strategyId || !blockId) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Alert severity="error">Missing project, strategy, or block ID</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleClose} sx={{ mb: 2 }}>
          Back to Strategy
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!resolvedBlockId) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleClose} sx={{ mb: 2 }}>
          Back to Strategy
        </Button>
        <Alert severity="error">Block not found</Alert>
      </Box>
    );
  }

  return (
    <OnboardingCanvasEditorV2
      open={true}
      onClose={handleClose}
      projectId={projectId}
      onboardingId=""
      versionId=""
      onboardingName={`${strategyName} > ${blockName}`}
      coordinatorFactory={() => new StrategyV2ScreenFlowCoordinator(projectId, strategyId, resolvedBlockId)}
      editorMode="ai"
      autoOpenScreenId={initialScreenId || undefined}
      hideLocaleTools
      strategyId={strategyId}
      blockId={resolvedBlockId}
      strategyBlockEntityId={resolvedBlockEntityId || resolvedBlockId}
      externalVariables={strategyVariables}
      onScreenEditorOpen={(screenId) => {
        navigate(buildBlockScreenCanvasUrl(projectId, strategyId, resolvedBlockId, screenId), { replace: true });
      }}
      onScreenEditorClose={() => {
        navigate(buildBlockCanvasUrl(projectId, strategyId, resolvedBlockId), { replace: true });
      }}
    />
  );
}

export default BlockCanvasPage;
